import React, { useEffect, useMemo, useRef, useState } from "react";
import { SHOP_LOGO_SIZES } from "./shopLogoTokens";
import PasswordInput from "./PasswordInput";
import { syncBrowserPushSubscription, notificationPermission } from "../utils/browserPush";
import {
  isMessageSoundEnabled,
  setMessageSoundEnabled,
  playMessageSound,
  unlockNotificationSound,
} from "../utils/notificationSound";
import { isDarkMode, applyTheme } from "../utils/theme";
import {
  NORTH_AMERICA_TIME_ZONE_GROUPS,
  TIME_ZONE_VALUES,
  getBrowserTimeZone,
  isValidTimeZone,
  formatNowForTimeZone,
  formatDateForTimeZone,
} from "../utils/timezones";
import {
  PHONE_COUNTRY_OPTIONS,
  DEFAULT_PHONE_COUNTRY,
  formatAsYouType,
  validatePhone,
  deriveCountryFromE164,
  formatStoredForDisplay,
} from "../utils/phone";
import MfaEnroll from "./MfaEnroll";
import { API_BASE } from "../utils/apiBase";
import { isNativePlatform } from "../utils/platform";
import { SHOP_SERVICE_GROUPS, cleanServices } from "../utils/shopServices";

const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];
const SERVICE_RADIUS_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 50, 75, 100, 150, 200, 250];

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

async function apiFetch(path, { method = "GET", token, body, onSessionExpired } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    data = await res.json();
  } else {
    const text = await res.text();
    data = text ? { message: text } : null;
  }

  if (!res.ok) {
    if ((res.status === 401 || res.status === 403) && typeof onSessionExpired === "function") {
      onSessionExpired("/login");
    }
    const msg =
      (data && (data.detail || data.message)) ||
      `Request failed: ${res.status} ${res.statusText}`;
    throw new Error(msg);
  }

  return data;
}

// Human-readable subscription status - shop owners should never see raw Stripe states
// like "incomplete" or "past_due" in monospace.
function describeSubscriptionStatus(me) {
  const status = (me?.shop_subscription_status || "").toLowerCase();
  const labels = {
    active: "Active",
    trialing: "Free trial",
    past_due: "Active - payment past due",
    unpaid: "Payment failed - subscription unpaid",
    incomplete: "Payment incomplete - finish checkout",
    incomplete_expired: "Checkout expired - start again",
    paused: "Paused",
    canceled: "Canceled",
  };
  if (labels[status]) return labels[status];
  return me?.shop_subscription_active ? "Active" : "Not subscribed";
}

// " (N days left)" suffix for the trial end date; empty when unknown/past.
function formatTrialDaysLeft(trialEndsAt) {
  const ends = new Date(trialEndsAt);
  if (Number.isNaN(ends.getTime())) return "";
  const daysLeft = Math.ceil((ends.getTime() - Date.now()) / 86400000);
  if (daysLeft < 0) return "";
  if (daysLeft === 0) return " (ends today)";
  return ` (${daysLeft} day${daysLeft === 1 ? "" : "s"} left)`;
}

function Card({ id, title, subtitle, children, right }) {
  return (
    <div id={id} className="bg-white/90 dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 shadow-sm rounded-2xl p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2
            id={id ? `${id}-heading` : undefined}
            tabIndex={-1}
            className="text-lg font-semibold text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-black/20 rounded"
          >
            {title}
          </h2>
          {subtitle ? (
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{subtitle}</p>
          ) : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-gray-800 dark:text-gray-100">{label}</label>
      {children}
      {hint ? <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p> : null}
    </div>
  );
}

function Input(props) {
  const className = cx(
    "w-full rounded-xl border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm",
    "focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-gray-400",
    props.className
  );
  // Password fields get a Show/Hide toggle; PasswordInput manages the type itself.
  // The trailing `className` overrides the one carried in {...props}.
  if (props.type === "password") {
    return <PasswordInput {...props} className={className} />;
  }
  return <input {...props} className={className} />;
}

function Button({ variant = "primary", className, ...props }) {
  const styles =
    variant === "primary"
      ? "bg-black text-white hover:bg-gray-900"
      : variant === "danger"
      ? "bg-red-600 text-white hover:bg-red-700"
      : variant === "ghost"
      ? "bg-transparent border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
      : "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600";

  return (
    <button
      {...props}
      className={cx(
        "rounded-xl px-4 py-2 text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed",
        styles,
        className
      )}
    />
  );
}

function Alert({ type = "info", children }) {
  const styles =
    type === "error"
      ? "border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 text-red-900 dark:text-red-200"
      : type === "success"
      ? "border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/40 text-green-900 dark:text-green-200"
      : type === "warning"
      ? "border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-gray-900 text-amber-900 dark:text-amber-200"
      : "border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200";
  return (
    <div className={cx("border rounded-xl px-3 py-2 text-sm", styles)}>
      {children}
    </div>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2.5 py-1 text-xs text-gray-800 dark:text-gray-100">
      {children}
    </span>
  );
}
function ShopSetupChecklistCard({ steps, completedCount, onGoToSection, onPreview, onDismiss, compact = false }) {
  const total = steps.length || 1;
  const pct = Math.round((completedCount / total) * 100);
  const nextStep = steps.find((step) => !step.done);

  function handleStep(step) {
    if (step.action === "preview") {
      onPreview?.();
      return;
    }
    onGoToSection?.(step.section || "shop-profile");
  }

  return (
    <div className="rounded-2xl border border-blue-200 dark:border-blue-900 bg-blue-50/80 dark:bg-blue-950/40 p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Shop setup checklist</div>
          <h2 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">Complete your shop setup</h2>
          <p className="mt-1 text-sm text-blue-950 dark:text-blue-200">
            {completedCount} of {steps.length} steps complete. Keep your shop card accurate so owners know when you are a good fit.
          </p>
        </div>
        {onDismiss ? (
          <button type="button" onClick={onDismiss} className="self-start text-xs font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-900">
            Dismiss
          </button>
        ) : null}
      </div>

      <div className="mt-3 h-2 overflow-hidden rounded-full bg-white dark:bg-gray-800 border border-blue-100">
        <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className={cx("mt-4 grid gap-2", compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1")}>
        {steps.map((step) => (
          <button
            key={step.id}
            type="button"
            onClick={() => handleStep(step)}
            className="flex items-start gap-3 rounded-xl border border-blue-100 bg-white dark:bg-gray-800 px-3 py-2 text-left hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40"
          >
            <span
              className={cx(
                "mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-xs font-bold",
                step.done ? "border-green-500 bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-300" : "border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300"
              )}
            >
              {step.done ? "✓" : "•"}
            </span>
            <span>
              <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">{step.label}</span>
              <span className="block text-xs text-gray-600 dark:text-gray-300">{step.description}</span>
            </span>
          </button>
        ))}
      </div>

      {nextStep ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button type="button" onClick={() => handleStep(nextStep)}>
            Continue setup
          </Button>
          <span className="text-xs text-blue-900 dark:text-blue-200">Next: {nextStep.label}</span>
        </div>
      ) : (
        <Alert type="success">Your shop setup checklist is complete. Revisit these steps any time your shop details change.</Alert>
      )}
    </div>
  );
}


export default function ProfilePage({ token: tokenProp, onLogout, onSessionExpired, initialSection = "", onInitialSectionApplied }) {
  const token = useMemo(() => tokenProp || localStorage.getItem("token") || "", [tokenProp]);
  const [pushBlocked, setPushBlocked] = useState(() => notificationPermission() === "denied");
  const [soundEnabled, setSoundEnabled] = useState(() => isMessageSoundEnabled());
  const [darkEnabled, setDarkEnabled] = useState(() => isDarkMode());
  const [mfa, setMfa] = useState(null); // { enabled, is_admin } | null while loading
  const [mfaEnrolling, setMfaEnrolling] = useState(false);
  const [mfaDisableCode, setMfaDisableCode] = useState("");

  const loadMfaStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/mfa/status`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setMfa(await res.json());
    } catch {}
  };
  useEffect(() => {
    loadMfaStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disableMfa = async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/mfa/disable`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ code: mfaDisableCode.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus({ type: "error", message: data?.detail || "Could not disable two-factor auth." });
        return;
      }
      setMfaDisableCode("");
      setStatus({ type: "success", message: "Two-factor authentication disabled." });
      await loadMfaStatus();
    } catch {
      setStatus({ type: "error", message: "Could not disable two-factor auth." });
    }
  };

  // Reflect theme changes made elsewhere (the floating corner toggle) in this setting.
  useEffect(() => {
    const onThemeChange = () => setDarkEnabled(isDarkMode());
    window.addEventListener("gc:themechange", onThemeChange);
    return () => window.removeEventListener("gc:themechange", onThemeChange);
  }, []);
  const api = (path, options = {}) =>
    apiFetch(path, { ...options, token, onSessionExpired });

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [status, setStatus] = useState({ type: "", message: "" });
  useEffect(() => {
    if (!status.message) return undefined;
    // Errors stay until the next action (a validation message that vanishes mid-scroll is
    // easy to miss on this long page); only success/info auto-dismiss.
    if (status.type === "error") return undefined;
    const timer = setTimeout(() => setStatus({ type: "", message: "" }), 7000);
    return () => clearTimeout(timer);
  }, [status]);

  const [shopSetupDismissed, setShopSetupDismissed] = useState(false);

  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
    time_zone: "UTC",

    shop_name: "",
    shop_general_repair: false,
    shop_specializations: [],
    shop_services: [],
    is_mobile_mechanic: false,
    mobile_service_radius_miles: 25,
    business_website_url: "",
    business_facebook_url: "",
    business_youtube_url: "",
    business_google_profile_url: "",

    message_email_notifications_enabled: true,
    message_browser_notifications_enabled: true,
    marketing_emails_enabled: true,
    shop_digest_emails_enabled: true,
    reminder_emails_enabled: true,
  });

  const shopSetupStorageKey = me?.id ? `gearcheckauto:shop-setup-dismissed:${me.id}` : "";
  const shopPreviewStorageKey = me?.id ? `gearcheckauto:shop-preview-viewed:${me.id}` : "";

  useEffect(() => {
    if (!me?.is_shop || !shopSetupStorageKey) {
      setShopSetupDismissed(false);
      return;
    }
    setShopSetupDismissed(localStorage.getItem(shopSetupStorageKey) === "true");
  }, [me?.id, me?.is_shop, shopSetupStorageKey]);

  const [shopPreviewViewed, setShopPreviewViewed] = useState(false);
  useEffect(() => {
    if (!me?.is_shop || !shopPreviewStorageKey) {
      setShopPreviewViewed(false);
      return;
    }
    setShopPreviewViewed(localStorage.getItem(shopPreviewStorageKey) === "true");
  }, [me?.id, me?.is_shop, shopPreviewStorageKey]);

  function dismissShopSetupChecklist() {
    setShopSetupDismissed(true);
    if (shopSetupStorageKey) localStorage.setItem(shopSetupStorageKey, "true");
  }

  function openShopPreview() {
    if (shopPreviewStorageKey) {
      localStorage.setItem(shopPreviewStorageKey, "true");
      setShopPreviewViewed(true);
    }
    window.history.pushState({}, "", "/shop-preview");
    window.dispatchEvent(new Event("popstate"));
  }

  const [savingProfile, setSavingProfile] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  // "Unsaved changes" tracking: snapshot the profile whenever `me` updates - the initial
  // load, saveProfile, and saveNotificationPref all setMe together with the matching
  // clean profile state, so an effect on [me] captures the saved shape in every case.
  const savedProfileSnapshotRef = useRef(null);
  useEffect(() => {
    if (!me) return;
    savedProfileSnapshotRef.current = JSON.stringify(profile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);
  const profileDirty =
    savedProfileSnapshotRef.current !== null &&
    JSON.stringify(profile) !== savedProfileSnapshotRef.current;
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoDeleting, setLogoDeleting] = useState(false);

  // TZ selector
  const [tzMode, setTzMode] = useState("preset");
  const [tzPreset, setTzPreset] = useState("UTC");
  const [tzCustom, setTzCustom] = useState("");
  const effectiveTz = tzMode === "preset" ? tzPreset : (tzCustom || "").trim();
  const tzOk = tzMode !== "custom" || isValidTimeZone(effectiveTz);

  // Phone number selector
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_PHONE_COUNTRY);
  const phoneCheck = validatePhone(profile.phone_number, phoneCountry);
  const phoneOk = phoneCheck.valid;

  // Makes UI (shop)
  const [allMakes, setAllMakes] = useState([]);
  const [makeSearch, setMakeSearch] = useState("");

  // Blocked users
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [pendingUnblock, setPendingUnblock] = useState(null);
  const [favoriteShops, setFavoriteShops] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [pendingUnfavorite, setPendingUnfavorite] = useState(null);

  // Email change
  const [emailStep, setEmailStep] = useState("idle");
  const [newEmail, setNewEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [emailCurrentPassword, setEmailCurrentPassword] = useState("");
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [confirmingEmailCode, setConfirmingEmailCode] = useState(false);

  // Account deletion verification
  const [deleteStep, setDeleteStep] = useState("idle");
  const [deleteCurrentPassword, setDeleteCurrentPassword] = useState("");
  const [deleteCode, setDeleteCode] = useState("");
  const [sendingDeleteCode, setSendingDeleteCode] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);

  // Password change
  const [pw, setPw] = useState({
    current_password: "",
    new_password: "",
    confirm_new_password: "",
  });
  const [changingPw, setChangingPw] = useState(false);
  const [pwStatus, setPwStatus] = useState({ type: "", message: "" });
  const [billingLoading, setBillingLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(initialSection || "");
  const activeSectionHeadingRef = useRef(null);

  async function loadBlockedUsers() {
    try {
      setBlocksLoading(true);
      const data = await api("/users/blocks", { token });

      // Expected: [{ blocked_user_id, blocked_user_display, blocked_at }]
      setBlockedUsers(Array.isArray(data) ? data : []);
    } catch {
      setBlockedUsers([]);
    } finally {
      setBlocksLoading(false);
    }
  }

  async function loadFavoriteShops() {
    try {
      setFavoritesLoading(true);
      const data = await api("/users/favorite-shops", { token });
      setFavoriteShops(Array.isArray(data) ? data : []);
    } catch {
      setFavoriteShops([]);
    } finally {
      setFavoritesLoading(false);
    }
  }

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setStatus({ type: "", message: "" });

        const data = await api("/users/me", { token });
        if (!mounted) return;

        setMe(data);

        const isShop = !!data.is_shop;

        const tz = (data.time_zone || getBrowserTimeZone() || "UTC").trim();
        const inPreset = TIME_ZONE_VALUES.includes(tz);
        setTzMode(inPreset ? "preset" : "custom");
        setTzPreset(inPreset ? tz : "UTC");
        setTzCustom(inPreset ? "" : tz);

        setPhoneCountry(deriveCountryFromE164(data.phone_number));

        setProfile({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          phone_number: formatStoredForDisplay(data.phone_number),
          street_address: data.street_address || "",
          city: data.city || "",
          state: data.state || "",
          zip_code: data.zip_code || "",
          time_zone: tz || "UTC",

          shop_name: isShop ? (data.shop_name || "") : "",
          shop_general_repair: isShop ? !!data.shop_general_repair : false,
          shop_specializations: isShop
            ? Array.isArray(data.shop_specializations)
              ? data.shop_specializations
              : []
            : [],
          shop_services: isShop
            ? Array.isArray(data.shop_services)
              ? data.shop_services
              : []
            : [],
          is_mobile_mechanic: isShop ? !!data.is_mobile_mechanic : false,
          mobile_service_radius_miles: isShop
            ? (data.mobile_service_radius_miles || 25)
            : 25,
          business_website_url: isShop ? (data.business_website_url || "") : "",
          business_facebook_url: isShop ? (data.business_facebook_url || "") : "",
          business_youtube_url: isShop ? (data.business_youtube_url || "") : "",
          business_google_profile_url: isShop ? (data.business_google_profile_url || "") : "",

          message_email_notifications_enabled:
            data.message_email_notifications_enabled ?? true,
          message_browser_notifications_enabled:
            data.message_browser_notifications_enabled ?? true,

          marketing_emails_enabled:
            data.marketing_emails_enabled ?? true,
          shop_digest_emails_enabled:
            data.shop_digest_emails_enabled ?? true,
          reminder_emails_enabled:
            data.reminder_emails_enabled ?? true,
        });

        await Promise.all([loadBlockedUsers(), loadFavoriteShops()]);

        if (isShop) {
          try {
            const makes = await apiFetch("/makes"); // via nginx
            if (!mounted) return;

            const normalized = Array.isArray(makes)
              ? makes
                  .map((m) => (typeof m === "string" ? m : m?.make || m?.name || ""))
                  .filter(Boolean)
              : [];
            normalized.sort((a, b) => a.localeCompare(b));
            setAllMakes(normalized);
          } catch {
            setAllMakes([]);
          }
        } else {
          setAllMakes([]);
        }
      } catch (e) {
        if (!mounted) return;
        setStatus({ type: "error", message: e.message });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
    // Load once per token. api, loadBlockedUsers, and loadFavoriteShops are redefined on every
    // render, so listing them here would re-run this fetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  function toggleSpecialization(make) {
    setProfile((p) => {
      const existing = new Set((p.shop_specializations || []).map((x) => String(x)));
      if (existing.has(make)) {
        return { ...p, shop_specializations: (p.shop_specializations || []).filter((x) => x !== make) };
      }
      return { ...p, shop_specializations: [...(p.shop_specializations || []), make] };
    });
  }

  function toggleService(service) {
    setProfile((p) => {
      const existing = new Set((p.shop_services || []).map((x) => String(x)));
      if (existing.has(service)) {
        return { ...p, shop_services: (p.shop_services || []).filter((x) => x !== service) };
      }
      return { ...p, shop_services: [...(p.shop_services || []), service] };
    });
  }

  function applyBrowserTimeZone() {
    const tz = (getBrowserTimeZone() || "UTC").trim();
    const inPreset = TIME_ZONE_VALUES.includes(tz);
    setTzMode(inPreset ? "preset" : "custom");
    setTzPreset(inPreset ? tz : "UTC");
    setTzCustom(inPreset ? "" : tz);
    setProfile((p) => ({ ...p, time_zone: tz }));
  }

  async function saveProfile() {
    try {
      setStatus({ type: "", message: "" });
      setSavingProfile(true);

      const firstName = (profile.first_name || "").trim();
      const lastName = (profile.last_name || "").trim();
      if (!firstName) {
        throw new Error("First name is required.");
      }
      if (!lastName) {
        throw new Error("Last name is required.");
      }

      if (me?.is_shop && !(profile.shop_name || "").trim()) {
        throw new Error("Shop name is required for shop accounts.");
      }

      // Mobile mechanics travel to the customer, so a street address isn't required -
      // only a shop name and a home-base ZIP (their service-area center).
      const isMobileMechanic = me?.is_shop && !!profile.is_mobile_mechanic;

      if (me?.is_shop && !isMobileMechanic && !(profile.street_address || "").trim()) {
        throw new Error("Street address is required for shop accounts.");
      }

      if (me?.is_shop && !isMobileMechanic && !(profile.city || "").trim()) {
        throw new Error("City is required for shop accounts.");
      }

      if (me?.is_shop && !isMobileMechanic && !(profile.state || "").trim()) {
        throw new Error("State is required for shop accounts.");
      }

      if (me?.is_shop && !(profile.zip_code || "").trim()) {
        throw new Error(
          isMobileMechanic
            ? "A home-base ZIP is required for mobile mechanics."
            : "ZIP code is required for shop accounts."
        );
      }

      const phoneResult = validatePhone(profile.phone_number, phoneCountry);
      if (!phoneResult.valid) {
        throw new Error("Enter a valid phone number, or clear the field.");
      }

      const shopName = (profile.shop_name || "").trim();
      const zipCode = (profile.zip_code || "").trim();

      const tzToSave = (effectiveTz || profile.time_zone || "UTC").trim() || "UTC";

      const body = {
        first_name: firstName,
        last_name: lastName,
        phone_number: phoneResult.e164,
        street_address: profile.street_address,
        city: profile.city,
        state: profile.state,
        zip_code: zipCode,

        time_zone: tzToSave,

        message_email_notifications_enabled: !!profile.message_email_notifications_enabled,
        message_browser_notifications_enabled: !!profile.message_browser_notifications_enabled,
        marketing_emails_enabled: !!profile.marketing_emails_enabled,
        reminder_emails_enabled: !!profile.reminder_emails_enabled,
      };

      if (me?.is_shop) {
        body.shop_digest_emails_enabled = !!profile.shop_digest_emails_enabled;
        body.shop_name = shopName;
        body.shop_general_repair = !!profile.shop_general_repair;
        body.is_mobile_mechanic = !!profile.is_mobile_mechanic;
        body.mobile_service_radius_miles = profile.is_mobile_mechanic
          ? Number(profile.mobile_service_radius_miles) || 25
          : null;

        const cleaned = (profile.shop_specializations || [])
          .map((s) => String(s).trim())
          .filter(Boolean);
        const deduped = Array.from(new Map(cleaned.map((s) => [s.toLowerCase(), s])).values());
        body.shop_specializations = deduped;
        body.shop_services = cleanServices(profile.shop_services);
        body.business_website_url = (profile.business_website_url || "").trim();
        body.business_facebook_url = (profile.business_facebook_url || "").trim();
        body.business_youtube_url = (profile.business_youtube_url || "").trim();
        body.business_google_profile_url = (profile.business_google_profile_url || "").trim();
      }

      let updated = null;
      try {
        updated = await api("/users/me", { method: "PATCH", token, body });
      } catch {
        updated = await api("/users/me", { method: "PUT", token, body });
      }

      setMe(updated);

      const savedTz = (updated.time_zone || tzToSave || "UTC").trim() || "UTC";
      const inPreset = TIME_ZONE_VALUES.includes(savedTz);
      setTzMode(inPreset ? "preset" : "custom");
      setTzPreset(inPreset ? savedTz : "UTC");
      setTzCustom(inPreset ? "" : savedTz);

      setProfile((p) => ({
        ...p,
        time_zone: savedTz,
        shop_name: me?.is_shop ? (updated.shop_name || "") : p.shop_name,
        shop_general_repair: me?.is_shop ? !!updated.shop_general_repair : p.shop_general_repair,
        shop_specializations:
          me?.is_shop && Array.isArray(updated.shop_specializations)
            ? updated.shop_specializations
            : p.shop_specializations,
        shop_services:
          me?.is_shop && Array.isArray(updated.shop_services)
            ? updated.shop_services
            : p.shop_services,
        is_mobile_mechanic: me?.is_shop
          ? !!updated.is_mobile_mechanic
          : p.is_mobile_mechanic,
        mobile_service_radius_miles: me?.is_shop
          ? (updated.mobile_service_radius_miles || 25)
          : p.mobile_service_radius_miles,
        business_website_url: me?.is_shop ? (updated.business_website_url || "") : p.business_website_url,
        business_facebook_url: me?.is_shop ? (updated.business_facebook_url || "") : p.business_facebook_url,
        business_youtube_url: me?.is_shop ? (updated.business_youtube_url || "") : p.business_youtube_url,
        business_google_profile_url: me?.is_shop ? (updated.business_google_profile_url || "") : p.business_google_profile_url,
        message_email_notifications_enabled:
          updated.message_email_notifications_enabled ?? p.message_email_notifications_enabled ?? true,
        message_browser_notifications_enabled:
          updated.message_browser_notifications_enabled ?? p.message_browser_notifications_enabled ?? true,
        marketing_emails_enabled:
          updated.marketing_emails_enabled ?? p.marketing_emails_enabled ?? true,
        shop_digest_emails_enabled:
          updated.shop_digest_emails_enabled ?? p.shop_digest_emails_enabled ?? true,
        reminder_emails_enabled:
          updated.reminder_emails_enabled ?? p.reminder_emails_enabled ?? true,
      }));

      if (shopPreviewStorageKey) {
        localStorage.removeItem(shopPreviewStorageKey);
        setShopPreviewViewed(false);
      }
      if (me?.is_shop && updated.geocode_warning) {
        setStatus({ type: "warning", message: "Profile saved, but your address couldn't be verified on the map. Your shop may not appear in location-based searches until the address is corrected." });
      } else {
        setStatus({ type: "success", message: "Profile saved." });
      }
    } catch (e) {
      setStatus({ type: "error", message: e.message });
    } finally {
      setSavingProfile(false);
    }
  }

  // Persist a single notification/marketing preference immediately (no Save button).
  // The caller applies the optimistic UI change first; `before` is the prior value so we
  // can revert if the request fails. Only the one changed field is sent, so the partial
  // PATCH never re-validates unrelated profile fields (name, ZIP, etc.).
  async function saveNotificationPref(field, checked, before) {
    const body = { [field]: checked };
    try {
      let updated = null;
      try {
        updated = await api("/users/me", { method: "PATCH", token, body });
      } catch {
        updated = await api("/users/me", { method: "PUT", token, body });
      }
      setMe(updated);
      setProfile((p) => ({ ...p, [field]: updated?.[field] ?? checked }));
      setStatus({ type: "success", message: "Saved." });
    } catch (e) {
      setProfile((p) => ({ ...p, [field]: before }));
      setStatus({
        type: "error",
        message: e.message || "Couldn't save that setting. Please try again.",
      });
    }
  }

  async function uploadShopLogo(file) {
    if (!file) return;
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setStatus({ type: "error", message: "Logo must be PNG, JPG, or WEBP." });
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setStatus({ type: "error", message: "Logo exceeds 5MB." });
      return;
    }

    try {
      setLogoUploading(true);
      setStatus({ type: "", message: "" });

      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API_BASE}/users/me/shop-logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Failed to upload logo.");
      setMe(data);
      setStatus({ type: "success", message: "Shop logo uploaded." });
    } catch (e) {
      setStatus({ type: "error", message: e.message || "Failed to upload logo." });
    } finally {
      setLogoUploading(false);
    }
  }

  async function deleteShopLogo() {
    try {
      setLogoDeleting(true);
      setStatus({ type: "", message: "" });
      const updated = await api("/users/me/shop-logo", { method: "DELETE" });
      setMe(updated);
      setStatus({ type: "success", message: "Shop logo deleted." });
    } catch (e) {
      setStatus({ type: "error", message: e.message || "Failed to delete logo." });
    } finally {
      setLogoDeleting(false);
    }
  }

  async function unblock() {
    if (!pendingUnblock?.id) return;

    try {
      await api(`/users/blocks/${pendingUnblock.id}`, { method: "DELETE", token });
      await loadBlockedUsers();
      setStatus({ type: "success", message: "User unblocked." });
    } catch (e) {
      setStatus({ type: "error", message: e.message });
    } finally {
      setPendingUnblock(null);
    }
  }

  async function unfavoriteShop() {
    if (!pendingUnfavorite?.id) return;

    try {
      await api(`/users/favorite-shops/${pendingUnfavorite.id}`, { method: "DELETE", token });
      await loadFavoriteShops();
      setStatus({ type: "success", message: "Shop removed from favorites." });
    } catch (e) {
      setStatus({ type: "error", message: e.message });
    } finally {
      setPendingUnfavorite(null);
    }
  }

  async function requestDeleteCode() {
    try {
      setStatus({ type: "", message: "" });
      setSendingDeleteCode(true);

      await api("/users/me/delete/request", {
        method: "POST",
        token,
        body: { current_password: deleteCurrentPassword },
      });

      setDeleteStep("requested");
      setStatus({ type: "success", message: "Verification code sent to your email." });
    } catch (e) {
      setStatus({ type: "error", message: e.message });
    } finally {
      setSendingDeleteCode(false);
    }
  }

  async function confirmDeleteAccount() {
    try {
      setRedirecting(true);
      setConfirmingDelete(true);
      setStatus({ type: "", message: "" });

      await api("/users/me/delete/confirm", {
        method: "POST",
        token,
        body: {
          current_password: deleteCurrentPassword,
          code: deleteCode,
        },
      });

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("verify.context");

      setStatus({ type: "success", message: "Account has been successfully deleted. Redirecting…" });

      setTimeout(() => {
        if (typeof onLogout === "function") onLogout();
        window.location.href = "/";
      }, 2200);
    } catch (e) {
      setRedirecting(false);
      setStatus({ type: "error", message: e.message });
    } finally {
      setConfirmingDelete(false);
      setShowDeleteAccountConfirm(false);
    }
  }

  async function requestEmailChange() {
    try {
      setStatus({ type: "", message: "" });
      setSendingEmailCode(true);

      await api("/users/me/email/change/request", {
        method: "POST",
        token,
        body: {
          new_email: newEmail,
          current_password: emailCurrentPassword,
        },
      });

      setEmailStep("requested");
      setStatus({ type: "success", message: "Verification code sent (check spam too)." });
    } catch (e) {
      setStatus({ type: "error", message: e.message });
    } finally {
      setSendingEmailCode(false);
    }
  }

  async function confirmEmailChange() {
    try {
      setStatus({ type: "", message: "" });
      setConfirmingEmailCode(true);

      await api("/users/me/email/change/confirm", {
        method: "POST",
        token,
        body: { code: emailCode },
      });

      setRedirecting(true);
      setStatus({ type: "success", message: "Email updated. Redirecting to sign in…" });

      localStorage.removeItem("token");
      localStorage.removeItem("user");

      setTimeout(() => {
        if (typeof onLogout === "function") onLogout();
        window.location.href = "/login";
      }, 900);
    } catch (e) {
      setStatus({ type: "error", message: e.message });
    } finally {
      setConfirmingEmailCode(false);
    }
  }

  async function changePassword() {
    try {
      setPwStatus({ type: "", message: "" });
      setChangingPw(true);

      if (!pw.current_password || !pw.new_password || !pw.confirm_new_password) {
        throw new Error("Please fill out all password fields.");
      }
      if (pw.new_password !== pw.confirm_new_password) {
        throw new Error("New password and confirmation do not match.");
      }

      await api("/users/me/password/change", {
        method: "POST",
        token,
        body: { current_password: pw.current_password, new_password: pw.new_password },
      });

      setPwStatus({ type: "success", message: "Password updated successfully." });
      setPw({ current_password: "", new_password: "", confirm_new_password: "" });
    } catch (e) {
      setPwStatus({ type: "error", message: e.message });
    } finally {
      setChangingPw(false);
    }
  }

  async function startSubscriptionCheckout() {
    // Web-only billing: the native apps never open an external (Stripe) checkout.
    if (isNativePlatform()) return;
    try {
      if (!me?.billing_configured) {
        throw new Error(me?.billing_config_message || "Billing is not configured.");
      }
      const status = (me?.shop_subscription_status || "").toLowerCase();
      const hasStripeSubscription = !!me?.stripe_subscription_id;
      const hasManagedSubscription =
        ["active", "past_due", "unpaid", "incomplete", "paused"].includes(status) ||
        (status === "trialing" && hasStripeSubscription);
      if (hasManagedSubscription) {
        throw new Error("An active subscription already exists. Use Billing portal to manage it.");
      }
      setBillingLoading(true);
      const data = await api("/users/me/billing/checkout", { method: "POST", token });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      setStatus({ type: "error", message: e.message });
    } finally {
      setBillingLoading(false);
    }
  }

  async function openBillingPortal() {
    // Web-only billing: the native apps never open the external Stripe billing portal.
    if (isNativePlatform()) return;
    try {
      if (!me?.billing_configured) {
        throw new Error(me?.billing_config_message || "Billing is not configured.");
      }
      setBillingLoading(true);
      const data = await api("/users/me/billing/portal", { method: "POST", token });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      setStatus({ type: "error", message: e.message });
    } finally {
      setBillingLoading(false);
    }
  }

  const subscriptionStatus = (me?.shop_subscription_status || "").toLowerCase();
  const hasStripeSubscription = !!me?.stripe_subscription_id;
  const hasExistingSubscription =
    ["active", "past_due", "unpaid", "incomplete", "paused"].includes(subscriptionStatus) ||
    (subscriptionStatus === "trialing" && hasStripeSubscription);

  const filteredMakes = allMakes.filter((m) => m.toLowerCase().includes((makeSearch || "").toLowerCase()));
  const shopSetupSteps = useMemo(() => {
    if (!me?.is_shop) return [];
    const hasLocation = [profile.street_address, profile.city, profile.state, profile.zip_code].every((v) => String(v || "").trim());
    const hasSpecializations = Array.isArray(profile.shop_specializations) && profile.shop_specializations.length > 0;
    const hasServices = Array.isArray(profile.shop_services) && profile.shop_services.length > 0;
    const hasBusinessLinks = [profile.business_website_url, profile.business_facebook_url, profile.business_youtube_url, profile.business_google_profile_url].some((v) => String(v || "").trim());
    return [
      {
        id: "identity-location",
        label: "Confirm shop name and location",
        description: "Owners use this to understand who you are and where you serve.",
        done: Boolean(String(profile.shop_name || "").trim() && hasLocation),
        section: "shop-profile",
      },
      {
        id: "logo-links",
        label: "Add logo and business links",
        description: "Make your card recognizable and give owners ways to learn more.",
        done: Boolean(me?.shop_logo_url && hasBusinessLinks),
        section: "shop-profile",
      },
      {
        id: "specializations",
        label: "Add make specializations",
        description: "Highlight the makes your shop knows best.",
        done: hasSpecializations,
        section: "shop-profile",
      },
      {
        id: "services",
        label: "Add services offered",
        description: "Show owners the services your shop provides.",
        done: hasServices,
        section: "shop-profile",
      },
      {
        id: "service-fit",
        label: "Confirm general repair coverage",
        description: "Let owners know whether you handle general repair requests too.",
        done: Boolean(profile.shop_general_repair || hasSpecializations),
        section: "shop-profile",
      },
      {
        id: "notifications",
        label: "Confirm message notifications",
        description: "Keep email or browser notifications on so new leads are not missed.",
        done: Boolean(profile.message_email_notifications_enabled || profile.message_browser_notifications_enabled),
        section: "notifications",
      },
      {
        id: "preview",
        label: "Preview your shop card",
        description: "Check the exact card owners see in shop search and requests.",
        done: shopPreviewViewed,
        action: "preview",
      },
    ];
  }, [me?.is_shop, me?.shop_logo_url, profile, shopPreviewViewed]);
  const completedShopSetupSteps = shopSetupSteps.filter((step) => step.done).length;
  const navGroups = useMemo(() => {
    const groups = [
      {
        id: "account",
        label: "Account",
        sections: [
          ...(!me?.is_shop ? [{ id: "personal-info", label: "Personal Info" }] : []),
          { id: "time-zone", label: "Time Zone" },
          { id: "change-email", label: "Change Email" },
          { id: "change-password", label: "Change Password" },
          { id: "two-factor", label: "Two-factor auth" },
        ],
      },
      {
        id: "appearance_group",
        label: "Appearance",
        sections: [{ id: "appearance", label: "Appearance" }],
      },
      {
        id: "communication",
        label: "Communication",
        sections: [
          { id: "notifications", label: "Message Notifications" },
          { id: "marketing", label: "Marketing & Newsletters" },
        ],
      },
      {
        id: "saved_blocked",
        label: "Saved & Blocked",
        sections: [
          { id: "favorite-shops", label: "Favorite Shops" },
          { id: "blocked-users", label: "Blocked Users" },
        ],
      },
    ];

    if (me?.is_shop) {
      groups.push({
        id: "shop_settings",
        label: "Shop Settings",
        sections: [
          { id: "shop-setup", label: "Setup Checklist" },
          { id: "shop-profile", label: "Shop Profile" },
          { id: "billing", label: "Billing" },
        ],
      });
    }

    groups.push({
      id: "account_deletion",
      label: "Account Deletion",
      sections: [{ id: "danger-zone", label: "Delete Account" }],
    });

    return groups;
  }, [me?.is_shop]);

  useEffect(() => {
    const allSectionIds = navGroups.flatMap((group) => group.sections.map((section) => section.id));
    if (!allSectionIds.includes(activeSection)) {
      setActiveSection("");
    }
  }, [activeSection, navGroups]);

  useEffect(() => {
    if (!initialSection) return;
    const allSectionIds = navGroups.flatMap((group) => group.sections.map((section) => section.id));
    if (allSectionIds.includes(initialSection)) {
      setActiveSection(initialSection);
      onInitialSectionApplied?.();
    }
  }, [initialSection, navGroups, onInitialSectionApplied]);

  function goToSection(sectionId) {
    setActiveSection(sectionId);
  }

  // On mobile, selecting a section swaps the menu for the detail view (CSS-driven);
  // bring it into view and move focus to its heading for keyboard/screen-reader users.
  useEffect(() => {
    if (!activeSection || typeof window === "undefined") return;
    const isMobile = typeof window.matchMedia === "function" && window.matchMedia("(max-width: 1023px)").matches;
    if (!isMobile) return;
    window.scrollTo({ top: 0 });
    window.requestAnimationFrame(() => {
      document.getElementById(`${activeSection}-heading`)?.focus();
    });
  }, [activeSection]);

  // Desktop keeps the detail panel filled even before an explicit pick (master-detail);
  // mobile stays menu-first (activeSection drives menu-vs-detail visibility below).
  const defaultSectionId = me?.is_shop ? "shop-profile" : "personal-info";
  const effectiveSection = activeSection || defaultSectionId;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1
            ref={activeSectionHeadingRef}
            tabIndex={-1}
            className="text-2xl font-bold text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-black/20 rounded"
          >
            Profile
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Update your profile details, email, and password.</p>
        </div>

        {status.message ? (
          <div className="mb-5">
            <Alert type={status.type || "info"}>{status.message}</Alert>
          </div>
        ) : null}

        {me?.is_shop && !shopSetupDismissed && !loading ? (
          <div className="mb-5">
            <ShopSetupChecklistCard
              steps={shopSetupSteps}
              completedCount={completedShopSetupSteps}
              onGoToSection={goToSection}
              onPreview={openShopPreview}
              onDismiss={dismissShopSetupChecklist}
              compact
            />
          </div>
        ) : null}

        {loading ? (
          <div className="text-sm text-gray-600 dark:text-gray-300">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-5 items-start">
            <aside
              className={cx(
                "lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 p-3 shadow-sm",
                "lg:block",
                activeSection ? "hidden" : "block"
              )}
            >
              <nav aria-label="Profile settings" className="space-y-4">
                {navGroups.map((group) => {
                  const isDanger = group.id === "account_deletion";
                  return (
                    <div key={group.id} className={cx(isDanger && "mt-1 pt-3 border-t border-gray-200 dark:border-gray-700")}>
                      <div className="text-xs font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide px-2 pb-1">
                        {group.label}
                      </div>
                      <div className="space-y-0.5">
                        {group.sections.map((section) => {
                          const active = effectiveSection === section.id;
                          return (
                            <button
                              key={section.id}
                              type="button"
                              onClick={() => goToSection(section.id)}
                              aria-current={active ? "page" : undefined}
                              className={cx(
                                "w-full text-left rounded-lg px-2.5 py-2.5 text-sm",
                                active
                                  ? "bg-gray-900 text-white"
                                  : isDanger
                                  ? "text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40"
                                  : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                              )}
                            >
                              {section.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </nav>
            </aside>

            <div
              tabIndex={-1}
              className={cx("grid-cols-1 gap-5 focus:outline-none", activeSection ? "grid" : "hidden lg:grid")}
            >
            {activeSection ? (
              <div className="lg:hidden sticky top-3 z-10">
                <button
                  type="button"
                  className="flex items-center gap-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-800/95 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm backdrop-blur hover:text-gray-900"
                  onClick={() => {
                    setActiveSection("");
                    window.scrollTo({ top: 0 });
                  }}
                >
                  ← Back to Profile Menu
                </button>
              </div>
            ) : null}
            {/* LEFT */}
            <div className="space-y-5">
              {me?.is_shop && effectiveSection === "shop-setup" ? (
                <ShopSetupChecklistCard
                  steps={shopSetupSteps}
                  completedCount={completedShopSetupSteps}
                  onGoToSection={goToSection}
                  onPreview={openShopPreview}
                  onDismiss={shopSetupDismissed ? null : dismissShopSetupChecklist}
                />
              ) : null}

              {me?.is_shop && effectiveSection === "billing" ? (
                <Card id="billing" title="Billing" subtitle="Manage your shop subscription and billing details.">
                  <div className="space-y-3 text-sm text-gray-700 dark:text-gray-200">
                    {!me?.billing_configured && me?.billing_config_message ? (
                      <Alert type="warning">{me.billing_config_message}</Alert>
                    ) : null}
                    {hasExistingSubscription ? (
                      <Alert type="info">This account already has a subscription. Use Billing portal to manage it.</Alert>
                    ) : null}
                    <div>Status: <span className="font-semibold">{describeSubscriptionStatus(me)}</span></div>
                    {me?.shop_trial_ends_at ? (
                      <div>
                        Trial ends: {formatDateForTimeZone(me.shop_trial_ends_at, profile.time_zone || me?.time_zone || "UTC")}
                        {formatTrialDaysLeft(me.shop_trial_ends_at)}
                      </div>
                    ) : null}
                    {me?.shop_subscription_whitelisted ? <Alert type="success">This account is permanently whitelisted from billing requirements.</Alert> : null}
                    {isNativePlatform() ? (
                      <Alert type="info">
                        Subscriptions are managed on the GearCheckAuto website. To start or manage your
                        shop subscription, sign in at gearcheckauto.com in a web browser.
                      </Alert>
                    ) : (
                      <div className="flex gap-2">
                        <Button onClick={startSubscriptionCheckout} disabled={billingLoading || !!me?.shop_subscription_whitelisted || !me?.billing_configured || hasExistingSubscription}>
                          {billingLoading ? "Loading…" : "Activate Subscription"}
                        </Button>
                        <Button variant="ghost" onClick={openBillingPortal} disabled={billingLoading || !me?.stripe_customer_id || !me?.billing_configured}>
                          Billing portal
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ) : null}

              {(effectiveSection === "personal-info" || effectiveSection === "shop-profile") ? (
              <Card
                id="personal-info"
                title="Profile details"
                subtitle="These details help match you with nearby shops."
                right={
                  <span className="inline-flex items-center gap-2">
                    {profileDirty && !savingProfile ? (
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Unsaved changes</span>
                    ) : null}
                    <Button
                      onClick={saveProfile}
                      disabled={savingProfile || redirecting || (tzMode === "custom" && !tzOk) || !phoneOk}
                    >
                      {savingProfile ? "Saving…" : "Save"}
                    </Button>
                  </span>
                }
              >
                <div className="space-y-5">
                  {me?.is_shop ? (
                    <Field label="Shop name" hint="Shown to customers when they message your shop.">
                      <Input
                        value={profile.shop_name}
                        onChange={(e) => setProfile((p) => ({ ...p, shop_name: e.target.value }))}
                        placeholder="Your shop name"
                        required={!!me?.is_shop}
                      />
                    </Field>
                  ) : null}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="First name">
                      <Input value={profile.first_name} onChange={(e) => setProfile((p) => ({ ...p, first_name: e.target.value }))} required />
                    </Field>
                    <Field label="Last name">
                      <Input value={profile.last_name} onChange={(e) => setProfile((p) => ({ ...p, last_name: e.target.value }))} required />
                    </Field>
                    <Field label="Phone number" hint="Optional. Used for SMS alerts later, so it must be a valid number.">
                      <div className="flex gap-2">
                        <select
                          className="rounded-xl border border-gray-300 dark:border-gray-600 px-2 py-2 text-sm bg-white dark:bg-gray-800 max-w-[9rem]"
                          value={phoneCountry}
                          onChange={(e) => setPhoneCountry(e.target.value)}
                          aria-label="Phone country"
                        >
                          {PHONE_COUNTRY_OPTIONS.map((opt) => (
                            <option key={opt.code} value={opt.code}>{opt.label}</option>
                          ))}
                        </select>
                        <Input
                          type="tel"
                          autoComplete="tel"
                          value={profile.phone_number}
                          onChange={(e) =>
                            setProfile((p) => ({ ...p, phone_number: formatAsYouType(e.target.value, phoneCountry) }))
                          }
                          placeholder="Optional"
                        />
                      </div>
                      {profile.phone_number && !phoneOk ? (
                        <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                          That phone number doesn&rsquo;t look valid for the selected country.
                        </p>
                      ) : null}
                    </Field>
                    <Field label="ZIP code">
                      <Input
                        value={profile.zip_code}
                        onChange={(e) => setProfile((p) => ({ ...p, zip_code: e.target.value }))}
                        placeholder={me?.is_shop ? "Required for shop accounts" : "Optional"}
                        required={!!me?.is_shop}
                      />
                    </Field>
                    <Field label="Street address">
                      <Input value={profile.street_address} onChange={(e) => setProfile((p) => ({ ...p, street_address: e.target.value }))} placeholder={me?.is_shop && !profile.is_mobile_mechanic ? "Required for shop accounts" : "Optional"} required={!!me?.is_shop && !profile.is_mobile_mechanic} />
                    </Field>
                    <Field label="City">
                      <Input value={profile.city} onChange={(e) => setProfile((p) => ({ ...p, city: e.target.value }))} placeholder={me?.is_shop && !profile.is_mobile_mechanic ? "Required for shop accounts" : "Optional"} required={!!me?.is_shop && !profile.is_mobile_mechanic} />
                    </Field>
                    <Field label="State">
                      <Input value={profile.state} onChange={(e) => setProfile((p) => ({ ...p, state: e.target.value }))} placeholder={me?.is_shop && !profile.is_mobile_mechanic ? "Required for shop accounts" : "Optional"} required={!!me?.is_shop && !profile.is_mobile_mechanic} />
                    </Field>
                    <Field label="Email (current)">
                      <Input value={me?.email || ""} disabled />
                    </Field>
                  </div>

                  {me?.is_shop ? (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Shop settings</div>
                        <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">Control general repair, make specializations, and services offered.</div>
                      </div>

                      <label
                        className={cx(
                          "mt-3 flex items-center gap-3 rounded-lg border p-3 cursor-pointer select-none",
                          profile.shop_general_repair
                            ? "border-gray-900 bg-gray-50 dark:bg-gray-900"
                            : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                        )}
                      >
                        <input
                          type="checkbox"
                          className="h-5 w-5 rounded border-gray-300 dark:border-gray-600"
                          checked={!!profile.shop_general_repair}
                          onChange={(e) => setProfile((p) => ({ ...p, shop_general_repair: e.target.checked }))}
                        />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">General auto repair (all makes)</span>
                      </label>

                      <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 space-y-2">
                        <label className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-100 select-none">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                            checked={!!profile.is_mobile_mechanic}
                            onChange={(e) =>
                              setProfile((p) => ({ ...p, is_mobile_mechanic: e.target.checked }))
                            }
                          />
                          <span className="font-semibold">
                            I'm a mobile mechanic (I travel to customers)
                          </span>
                        </label>
                        <div className="text-xs text-gray-600 dark:text-gray-300">
                          Mobile mechanics appear on the map as a general service area around your ZIP -
                          not a street address - so a street address isn't required. Set how far you'll travel.
                        </div>
                        {profile.is_mobile_mechanic ? (
                          <label className="block text-sm font-medium text-gray-800 dark:text-gray-100">
                            Service radius
                            <select
                              className="mt-1 w-full sm:w-48 border border-gray-300 dark:border-gray-600 rounded-lg p-2 bg-white dark:bg-gray-800"
                              value={profile.mobile_service_radius_miles || 25}
                              onChange={(e) =>
                                setProfile((p) => ({
                                  ...p,
                                  mobile_service_radius_miles: Number(e.target.value),
                                }))
                              }
                            >
                              {SERVICE_RADIUS_OPTIONS.map((r) => (
                                <option key={r} value={r}>
                                  {r} miles
                                </option>
                              ))}
                            </select>
                          </label>
                        ) : null}
                      </div>

                      <div className="mt-4 space-y-3">
                        <Field label="Shop logo" hint="Optional. PNG/JPG/WEBP up to 5MB. Larger images are auto-resized.">
                          <div className="flex items-center gap-3 flex-wrap">
                            <div
                              className="rounded border bg-white dark:bg-gray-800 overflow-hidden flex items-center justify-center"
                              style={{ width: SHOP_LOGO_SIZES.profilePreview, height: SHOP_LOGO_SIZES.profilePreview }}
                            >
                              {me?.shop_logo_url ? (
                                <img src={me.shop_logo_url} alt="Shop logo" className="h-full w-full object-contain" />
                              ) : (
                                <span className="text-xs text-gray-500 dark:text-gray-400">No logo</span>
                              )}
                            </div>
                            <label className="text-sm px-3 py-2 border rounded-xl cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                              {logoUploading ? "Uploading…" : "Upload logo"}
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                className="hidden"
                                disabled={logoUploading || logoDeleting}
                                onChange={(e) => {
                                  const file = e.target.files?.[0] || null;
                                  e.target.value = "";
                                  uploadShopLogo(file);
                                }}
                              />
                            </label>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={deleteShopLogo}
                              disabled={!me?.shop_logo_url || logoUploading || logoDeleting}
                            >
                              {logoDeleting ? "Deleting…" : "Delete logo"}
                            </Button>
                          </div>
                        </Field>

                        <Field label="Make specializations" hint="Optional. Select the makes your shop specializes in.">
                          <Input value={makeSearch} onChange={(e) => setMakeSearch(e.target.value)} placeholder="Search makes…" />

                          <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-xl p-3 max-h-56 overflow-auto bg-white dark:bg-gray-800">
                            {filteredMakes.length ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {filteredMakes.map((mk) => {
                                  const checked = (profile.shop_specializations || []).includes(mk);
                                  return (
                                    <label
                                      key={mk}
                                      className={cx(
                                        "flex items-center gap-2 rounded-lg border px-2.5 py-2 text-sm cursor-pointer",
                                        checked ? "border-gray-900 bg-gray-50 dark:bg-gray-900" : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                                      )}
                                    >
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                                        checked={checked}
                                        onChange={() => toggleSpecialization(mk)}
                                      />
                                      <span className="truncate">{mk}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-600 dark:text-gray-300">No makes found.</div>
                            )}
                          </div>

                          {(profile.shop_specializations || []).length ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {profile.shop_specializations.map((mk) => (
                                <Pill key={mk}>
                                  {mk}
                                  <button
                                    type="button"
                                    onClick={() => toggleSpecialization(mk)}
                                    className="text-gray-500 dark:text-gray-400 hover:text-gray-900"
                                    aria-label={`Remove ${mk}`}
                                  >
                                    ×
                                  </button>
                                </Pill>
                              ))}
                            </div>
                          ) : null}
                        </Field>

                        <Field label="Services offered" hint="Optional. Select the services your shop provides.">
                          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 max-h-64 overflow-auto bg-white dark:bg-gray-800 space-y-3">
                            {SHOP_SERVICE_GROUPS.map((group) => (
                              <div key={group.label}>
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                                  {group.label}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {group.services.map((svc) => {
                                    const checked = (profile.shop_services || []).includes(svc);
                                    return (
                                      <label
                                        key={svc}
                                        className={cx(
                                          "flex items-center gap-2 rounded-lg border px-2.5 py-2 text-sm cursor-pointer",
                                          checked ? "border-gray-900 bg-gray-50 dark:bg-gray-900" : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                                        )}
                                      >
                                        <input
                                          type="checkbox"
                                          className="h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                                          checked={checked}
                                          onChange={() => toggleService(svc)}
                                        />
                                        <span className="truncate">{svc}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>

                          {(profile.shop_services || []).length ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {profile.shop_services.map((svc) => (
                                <Pill key={svc}>
                                  {svc}
                                  <button
                                    type="button"
                                    onClick={() => toggleService(svc)}
                                    className="text-gray-500 dark:text-gray-400 hover:text-gray-900"
                                    aria-label={`Remove ${svc}`}
                                  >
                                    ×
                                  </button>
                                </Pill>
                              ))}
                            </div>
                          ) : null}
                        </Field>

                        <div className="pt-2">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Business Links</div>
                          <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                            Optional. Add links to your website, Facebook page, YouTube channel, and Google Business Profile.
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Field label="Website">
                            <Input
                              type="url"
                              value={profile.business_website_url}
                              onChange={(e) => setProfile((p) => ({ ...p, business_website_url: e.target.value }))}
                              placeholder="https://yourshop.com"
                            />
                          </Field>
                          <Field label="Facebook">
                            <Input
                              type="url"
                              value={profile.business_facebook_url}
                              onChange={(e) => setProfile((p) => ({ ...p, business_facebook_url: e.target.value }))}
                              placeholder="https://facebook.com/yourpage"
                            />
                          </Field>
                          <Field label="YouTube">
                            <Input
                              type="url"
                              value={profile.business_youtube_url}
                              onChange={(e) => setProfile((p) => ({ ...p, business_youtube_url: e.target.value }))}
                              placeholder="https://youtube.com/@yourchannel"
                            />
                          </Field>
                          <Field
                            label="Google Business Profile"
                            hint="Powers your star rating. To get the exact link: on Google Maps, open your business, tap Share, then Copy link."
                          >
                            <Input
                              type="url"
                              value={profile.business_google_profile_url}
                              onChange={(e) => setProfile((p) => ({ ...p, business_google_profile_url: e.target.value }))}
                              placeholder="https://maps.app.goo.gl/... (Share > Copy link)"
                            />
                          </Field>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Second Save at the bottom of the (long) form so shops editing services
                      or links do not have to scroll back to the card header to save. */}
                  <div className="flex items-center justify-end gap-2 border-t border-gray-200 dark:border-gray-700 pt-4">
                    {profileDirty && !savingProfile ? (
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Unsaved changes</span>
                    ) : null}
                    <Button
                      onClick={saveProfile}
                      disabled={savingProfile || redirecting || (tzMode === "custom" && !tzOk) || !phoneOk}
                    >
                      {savingProfile ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </div>
              </Card>
              ) : null}

              {/* Danger zone (left) */}
              {effectiveSection === "danger-zone" ? (
              <Card id="danger-zone" title="Danger zone" subtitle="Delete your account and permanently remove your data.">
                <div className="space-y-3">
                  <div className="text-xs text-gray-600 dark:text-gray-300">
                    {me?.is_shop
                      ? "This will permanently delete your account and messages, and cancel any active subscription billing. This cannot be undone."
                      : "This will permanently delete your account and messages. This cannot be undone."}
                  </div>

                  <Field label="Current password" hint="Required before we email you a deletion verification code.">
                    <Input
                      type="password"
                      value={deleteCurrentPassword}
                      onChange={(e) => setDeleteCurrentPassword(e.target.value)}
                      disabled={sendingDeleteCode || confirmingDelete || redirecting}
                    />
                  </Field>

                  <Button
                    variant="ghost"
                    onClick={requestDeleteCode}
                    disabled={!deleteCurrentPassword || sendingDeleteCode || confirmingDelete || redirecting}
                  >
                    {sendingDeleteCode ? "Sending code…" : "Send deletion code"}
                  </Button>

                  {deleteStep === "requested" ? (
                    <Field label="Verification code" hint="Enter the code sent to your account email address.">
                      <Input
                        value={deleteCode}
                        onChange={(e) => setDeleteCode(e.target.value)}
                        disabled={confirmingDelete || redirecting}
                      />
                    </Field>
                  ) : null}

                  <Button
                    variant="danger"
                    onClick={() => setShowDeleteAccountConfirm(true)}
                    disabled={!deleteCurrentPassword || !deleteCode || deleteStep !== "requested" || confirmingDelete || redirecting}
                  >
                    {confirmingDelete ? "Deleting…" : "Delete Account"}
                  </Button>
                </div>
              </Card>
              ) : null}
            </div>

            {/* RIGHT */}
            <div className="space-y-5">
              {effectiveSection === "time-zone" ? (
              <Card id="time-zone" title="Time zone" subtitle="Choose the time zone used to display message timestamps.">
                <div className="space-y-3">
                  <Field label="Time zone" hint="Pick from the list, or choose Custom… for any IANA time zone.">
                    <select
                      className="w-full rounded-xl border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-800"
                      value={tzMode === "preset" ? tzPreset : "__custom__"}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "__custom__") {
                          setTzMode("custom");
                          setTzCustom(tzCustom || "");
                          setProfile((p) => ({ ...p, time_zone: tzCustom || p.time_zone || "UTC" }));
                        } else {
                          setTzMode("preset");
                          setTzPreset(v);
                          setTzCustom("");
                          setProfile((p) => ({ ...p, time_zone: v }));
                        }
                      }}
                      disabled={savingProfile || redirecting}
                    >
                      {NORTH_AMERICA_TIME_ZONE_GROUPS.map((group) => (
                        <optgroup key={group.region} label={group.region}>
                          {group.zones.map((zone) => (
                            <option key={zone.value} value={zone.value}>{zone.label}</option>
                          ))}
                        </optgroup>
                      ))}
                      <option value="__custom__">Custom…</option>
                    </select>

                    {tzMode === "custom" ? (
                      <div className="mt-2">
                        <Input
                          value={tzCustom}
                          onChange={(e) => {
                            const v = e.target.value;
                            setTzCustom(v);
                            setProfile((p) => ({ ...p, time_zone: v }));
                          }}
                          placeholder="e.g. America/Chicago"
                          disabled={savingProfile || redirecting}
                        />
                        {!tzOk ? (
                          <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                            That time zone doesn’t look valid. Example:{" "}
                            <span className="font-mono">America/Chicago</span>
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </Field>

                  <div className="flex items-center gap-2">
                    <Button type="button" onClick={applyBrowserTimeZone} disabled={savingProfile || redirecting}>
                      Use browser time zone
                    </Button>
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                      Preview: {formatNowForTimeZone(profile.time_zone || "UTC")}
                    </div>
                  </div>

                  <div>
                    <Button onClick={saveProfile} disabled={savingProfile || redirecting || (tzMode === "custom" && !tzOk)}>
                      {savingProfile ? "Saving…" : "Save time zone"}
                    </Button>
                  </div>
                </div>
              </Card>
              ) : null}

              {effectiveSection === "two-factor" ? (
              <Card
                id="two-factor"
                title="Two-factor authentication"
                subtitle="Require a code from an authenticator app at login for stronger account security."
              >
                {mfa == null ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
                ) : mfa.enabled ? (
                  <div className="space-y-3">
                    <div className="inline-flex items-center gap-2 rounded-lg border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/40 px-3 py-2 text-sm text-green-800 dark:text-green-300">
                      Two-factor authentication is on.
                    </div>
                    {mfa.is_admin ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Your account is an admin, so two-factor authentication can't be turned off.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-700 dark:text-gray-200">
                          To turn it off, enter a current code from your app (or a recovery code).
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <input
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="123456"
                            value={mfaDisableCode}
                            onChange={(e) => setMfaDisableCode(e.target.value)}
                            className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm tracking-widest"
                          />
                          <button
                            type="button"
                            onClick={disableMfa}
                            disabled={!mfaDisableCode.trim()}
                            className="rounded-xl border border-red-300 dark:border-red-900 px-4 py-2 text-sm font-semibold text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-60"
                          >
                            Turn off
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : mfaEnrolling ? (
                  <MfaEnroll
                    token={token}
                    onComplete={() => {
                      setMfaEnrolling(false);
                      loadMfaStatus();
                      setStatus({ type: "success", message: "Two-factor authentication enabled." });
                    }}
                    onCancel={() => setMfaEnrolling(false)}
                  />
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Two-factor authentication is off. Add it for stronger account security.
                    </p>
                    <button
                      type="button"
                      onClick={() => setMfaEnrolling(true)}
                      className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
                    >
                      Enable two-factor authentication
                    </button>
                  </div>
                )}
              </Card>
              ) : null}

              {effectiveSection === "appearance" ? (
              <Card
                id="appearance"
                title="Appearance"
                subtitle="Choose how GearCheckAuto looks. This preference is saved on this device."
              >
                <label className="flex items-start gap-3 select-none">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                    checked={darkEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setDarkEnabled(checked);
                      applyTheme(checked ? "dark" : "light");
                    }}
                  />
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Dark mode
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                      Use a dark color theme across the site. You can also use the round toggle
                      button in the bottom corner of any page.
                    </div>
                  </div>
                </label>
              </Card>
              ) : null}

              {effectiveSection === "notifications" ? (
              <Card
                id="notifications"
                title="Notifications"
                subtitle="Control email and browser notifications for messages. Changes save automatically. System emails (verification, password resets) are always sent."
              >
                <label className="flex items-start gap-3 select-none">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                    checked={!!profile.message_email_notifications_enabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const before = !!profile.message_email_notifications_enabled;
                      setProfile((p) => ({
                        ...p,
                        message_email_notifications_enabled: checked,
                      }));
                      saveNotificationPref(
                        "message_email_notifications_enabled",
                        checked,
                        before
                      );
                    }}
                    disabled={redirecting}
                  />
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Email me when I receive a new message
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                      Turning this off will stop “New Message in Your Inbox” emails, but won’t affect
                      account verification or password reset emails.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 select-none mt-5">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                    checked={!!profile.message_browser_notifications_enabled}
                    onChange={async (e) => {
                      const checked = e.target.checked;
                      const before = !!profile.message_browser_notifications_enabled;
                      setProfile((p) => ({
                        ...p,
                        message_browser_notifications_enabled: checked,
                      }));
                      // User gesture → may prompt for permission and (un)subscribe this device.
                      try {
                        const res = await syncBrowserPushSubscription(token, checked, {
                          promptIfNeeded: true,
                        });
                        setPushBlocked(checked && res.permission === "denied");
                      } catch {}
                      saveNotificationPref(
                        "message_browser_notifications_enabled",
                        checked,
                        before
                      );
                    }}
                    disabled={redirecting}
                  />
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Show browser notifications for new messages
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                      Get notified of new messages even when GearCheckAuto is closed (on this device/browser).
                    </div>
                    {pushBlocked ? (
                      <div className="text-xs text-red-700 dark:text-red-300 mt-1">
                        Notifications are blocked for this site in your browser. Enable them in your
                        browser's site settings, then toggle this back on.
                      </div>
                    ) : null}
                  </div>
                </label>
                <label className="flex items-start gap-3 select-none mt-5">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                    checked={soundEnabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSoundEnabled(checked);
                      setMessageSoundEnabled(checked);
                      if (checked) {
                        // User gesture → unlock audio and play a sample so they hear it.
                        unlockNotificationSound();
                        playMessageSound();
                      }
                    }}
                  />
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Play a sound for new messages (this browser)
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                      Plays a short chime when a new message arrives while you're on the Messages page,
                      on this device. Saved per browser and independent of the email / browser-notification
                      settings above.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 select-none mt-5">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                    checked={!!profile.reminder_emails_enabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const before = !!profile.reminder_emails_enabled;
                      setProfile((p) => ({
                        ...p,
                        reminder_emails_enabled: checked,
                      }));
                      saveNotificationPref(
                        "reminder_emails_enabled",
                        checked,
                        before
                      );
                    }}
                    disabled={redirecting}
                  />
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Send me reminder emails
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                      Reminders about unread shop replies, quotes awaiting your decision, and upcoming
                      appointments. Does not affect new-message or account emails.
                    </div>
                  </div>
                </label>
              </Card>
              ) : null}

              {effectiveSection === "marketing" ? (
              <Card
                id="marketing"
                title="Promotions & newsletters"
                subtitle="Control whether you receive promotions, product updates, and newsletters. Changes save automatically."
              >
                <label className="flex items-start gap-3 select-none">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                    checked={!!profile.marketing_emails_enabled}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const before = !!profile.marketing_emails_enabled;
                      setProfile((p) => ({
                        ...p,
                        marketing_emails_enabled: checked,
                      }));
                      saveNotificationPref(
                        "marketing_emails_enabled",
                        checked,
                        before
                      );
                    }}
                    disabled={redirecting}
                  />
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Send me promotions and newsletters
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                      Transactional emails (password reset, verification) are not affected.
                    </div>
                  </div>
                </label>

                {me?.is_shop ? (
                  <label className="flex items-start gap-3 select-none mt-5">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600"
                      checked={!!profile.shop_digest_emails_enabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const before = !!profile.shop_digest_emails_enabled;
                        setProfile((p) => ({
                          ...p,
                          shop_digest_emails_enabled: checked,
                        }));
                        saveNotificationPref(
                          "shop_digest_emails_enabled",
                          checked,
                          before
                        );
                      }}
                      disabled={redirecting}
                    />
                    <div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Send me weekly performance digest emails
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                        Weekly summary of leads received, response rate, and booking rate for your shop.
                      </div>
                    </div>
                  </label>
                ) : null}
              </Card>
              ) : null}

              {/* ✅ Blocked users restored */}
              {effectiveSection === "blocked-users" ? (
              <Card
                id="blocked-users"
                title="Blocked users"
                subtitle="Manage users you have blocked. Unblocking will allow new messages from them again."
                right={
                  <Button type="button" variant="ghost" onClick={loadBlockedUsers} disabled={blocksLoading || redirecting}>
                    {blocksLoading ? "Refreshing…" : "Refresh"}
                  </Button>
                }
              >
                {blocksLoading ? (
                  <div className="text-sm text-gray-600 dark:text-gray-300">Loading blocked users…</div>
                ) : blockedUsers.length === 0 ? (
                  <div className="text-sm text-gray-600 dark:text-gray-300">No blocked users.</div>
                ) : (
                  <div className="space-y-2">
                    {blockedUsers.map((b) => {
                      const label =
                        b.blocked_user_display ||
                        b.blocked_name ||
                        b.blocked_email ||
                        String(b.blocked_user_id);

                      const ts =
                        b.blocked_at || b.created_at || null;

                      return (
                        <div
                          key={String(b.blocked_user_id)}
                          className="flex items-center justify-between gap-3 border rounded-xl p-3 bg-white dark:bg-gray-800"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{label}</div>
                            {ts ? (
                              <div className="text-xs text-gray-600 dark:text-gray-300">
                                Blocked: {new Date(ts).toLocaleString()}
                              </div>
                            ) : null}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setPendingUnblock({ id: b.blocked_user_id, label })}
                            disabled={redirecting}
                          >
                            Unblock
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
              ) : null}

              {effectiveSection === "favorite-shops" ? (
              <Card
                id="favorite-shops"
                title="Favorite shops"
                subtitle="Manage shop users you have favorited."
                right={
                  <Button type="button" variant="ghost" onClick={loadFavoriteShops} disabled={favoritesLoading || redirecting}>
                    {favoritesLoading ? "Refreshing…" : "Refresh"}
                  </Button>
                }
              >
                {favoritesLoading ? (
                  <div className="text-sm text-gray-600 dark:text-gray-300">Loading favorite shops…</div>
                ) : favoriteShops.length === 0 ? (
                  <div className="text-sm text-gray-600 dark:text-gray-300">No favorite shops.</div>
                ) : (
                  <div className="space-y-2">
                    {favoriteShops.map((shop) => {
                      const label =
                        shop.shop_user_display ||
                        shop.shop_name ||
                        String(shop.shop_user_id);
                      const ts = shop.favorited_at || shop.created_at || null;

                      return (
                        <div
                          key={String(shop.shop_user_id)}
                          className="flex items-center justify-between gap-3 border rounded-xl p-3 bg-white dark:bg-gray-800"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{label}</div>
                            {ts ? (
                              <div className="text-xs text-gray-600 dark:text-gray-300">
                                Favorited: {new Date(ts).toLocaleString()}
                              </div>
                            ) : null}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setPendingUnfavorite({ id: shop.shop_user_id, label })}
                            disabled={redirecting}
                          >
                            Remove
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
              ) : null}

              {effectiveSection === "change-email" ? (
              <Card id="change-email" title="Change email" subtitle="Enter your current password, then we’ll send a verification code to your new email address.">
                <div className="space-y-4">
                  <Field label="Current password">
                    <Input
                      type="password"
                      value={emailCurrentPassword}
                      onChange={(e) => setEmailCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                      disabled={redirecting}
                    />
                  </Field>

                  <Field label="New email address">
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="name@example.com"
                      disabled={redirecting}
                    />
                  </Field>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      onClick={requestEmailChange}
                      disabled={sendingEmailCode || !newEmail || !emailCurrentPassword || redirecting}
                    >
                      {sendingEmailCode ? "Sending…" : "Send code"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setEmailStep("idle");
                        setNewEmail("");
                        setEmailCode("");
                        setEmailCurrentPassword("");
                      }}
                      disabled={sendingEmailCode || confirmingEmailCode || redirecting}
                    >
                      Reset
                    </Button>
                  </div>

                  {emailStep === "requested" ? (
                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                      <Field label="Verification code">
                        <Input
                          value={emailCode}
                          onChange={(e) => setEmailCode(e.target.value)}
                          placeholder="6-digit code"
                          inputMode="numeric"
                          disabled={redirecting}
                        />
                      </Field>
                      <div className="mt-3">
                        <Button
                          type="button"
                          onClick={confirmEmailChange}
                          disabled={confirmingEmailCode || !emailCode || redirecting}
                        >
                          {confirmingEmailCode ? "Confirming…" : "Confirm email change"}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </Card>
              ) : null}

              {effectiveSection === "change-password" ? (
              <Card
                id="change-password"
                title="Change password"
                subtitle="Password must be 12+ characters and include upper, lower, number, and special character."
              >
                <div className="space-y-4">
                  {pwStatus.message ? <Alert type={pwStatus.type}>{pwStatus.message}</Alert> : null}

                  <Field label="Current password">
                    <Input
                      type="password"
                      value={pw.current_password}
                      onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))}
                      autoComplete="current-password"
                    />
                  </Field>

                  <Field label="New password">
                    <Input
                      type="password"
                      value={pw.new_password}
                      onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))}
                      autoComplete="new-password"
                    />
                  </Field>

                  <Field label="Confirm new password">
                    <Input
                      type="password"
                      value={pw.confirm_new_password}
                      onChange={(e) => setPw((p) => ({ ...p, confirm_new_password: e.target.value }))}
                      autoComplete="new-password"
                    />
                  </Field>

                  <div className="flex gap-3">
                    <Button type="button" onClick={changePassword} disabled={changingPw || redirecting}>
                      {changingPw ? "Updating…" : "Update password"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setPw({ current_password: "", new_password: "", confirm_new_password: "" })}
                      disabled={changingPw || redirecting}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </Card>
              ) : null}
            </div>
          </div>
          </div>
        )}

        {pendingUnblock && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {`Unblock ${pendingUnblock.label}? They will be able to deliver new messages to you.`}
              </p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={unblock}
                  className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-check not-italic" />
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setPendingUnblock(null)}
                  className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-cross not-italic" />
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {pendingUnfavorite && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {`Remove ${pendingUnfavorite.label} from favorite shops?`}
              </p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={unfavoriteShop}
                  className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-check not-italic" />
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setPendingUnfavorite(null)}
                  className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-cross not-italic" />
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {showDeleteAccountConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-800 p-5 shadow-xl">
              <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Delete your account? This will permanently remove your account and messages. This cannot be undone.
              </p>
              <div className="mt-4 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={confirmDeleteAccount}
                  className="bg-green-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-check not-italic" />
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteAccountConfirm(false)}
                  className="bg-red-500 text-white px-4 py-2 rounded-xl inline-flex items-center justify-center gap-2 hover:brightness-95 active:scale-[0.98]"
                >
                  <i className="fi fi-rr-cross not-italic" />
                  No
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
